import { useEffect, useRef, useState } from 'react';

// First-run welcome (D-LAUNCH1): four full-screen cards, shown once per
// signed-in tech and replayable from Help. Swipe, tap Next, or use the arrow
// keys. No animation, so there is no motion to reduce.
const CARDS = [
  {
    icon: '🔧',
    title: 'Welcome to FaultFinder',
    body: [
      'Scan the data tag, get the right manuals, and troubleshoot with an AI that walks you through one test at a time.',
      'Every tech sheet you save helps every tech on the next call for that model.',
    ],
  },
  {
    icon: '📷',
    title: 'Scan the tag',
    body: [
      'Tap Scan Tag and photograph the data tag — flat, whole label in frame, no glare on the model number.',
      'A field wrong? Tap the pencil on the Result screen and fix it. No need to re-shoot.',
    ],
  },
  {
    icon: '🩺',
    title: 'Troubleshoot one step at a time',
    body: [
      'Describe the symptom, then answer one question at a time. Type a meter reading and it shows PASS or FAIL before you send.',
      'Short on time? Tap 🎯 Give me your best call. When you finish, answer "Was the diagnosis right?" — that is how it gets better.',
    ],
  },
  {
    icon: '🧪',
    title: "You're a tester",
    body: [
      'Something broke, confused you, or looked wrong? Tap Feedback at the top of any screen. Dictate it if your hands are dirty.',
      'Rate the AI\'s replies with 👍 / 👎. Your reports decide what we fix next.',
    ],
  },
];

// Touches starting this close to the left edge are Safari's own back-swipe
// gesture — let them through instead of hijacking them as a card swipe.
const EDGE_GUARD_PX = 20;

export default function Welcome({ name = '', onDone }) {
  const [index, setIndex] = useState(0);
  const startX = useRef(null);
  const startY = useRef(null);
  const titleRef = useRef(null);
  const card = CARDS[index];
  const last = index === CARDS.length - 1;

  useEffect(() => {
    titleRef.current?.focus();
  }, [index]);

  function next() {
    if (last) onDone();
    else setIndex(index + 1);
  }
  function back() {
    if (index > 0) setIndex(index - 1);
  }
  function onTouchStart(e) {
    const x = e.touches[0].clientX;
    if (x <= EDGE_GUARD_PX) {
      startX.current = null;
      startY.current = null;
      return;
    }
    startX.current = x;
    startY.current = e.touches[0].clientY;
  }
  function onTouchEnd(e) {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    const dy = e.changedTouches[0].clientY - startY.current;
    startX.current = null;
    startY.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > 1.5 * Math.abs(dy)) {
      if (dx < 0) next();
      else back();
    }
  }
  function onKeyDown(e) {
    if (e.key === 'Escape') onDone();
    else if (e.key === 'ArrowRight') next();
    else if (e.key === 'ArrowLeft') back();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      className="fixed inset-0 z-50 bg-ink-900 flex flex-col overscroll-contain"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKeyDown}
    >
      <div className="flex justify-end p-4">
        <button type="button" onClick={onDone} className="btn-ghost min-h-[60px] px-5 text-base">
          Skip
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center px-8 text-center max-w-2xl mx-auto">
        <div className="my-auto w-full">
          <div className="text-7xl mb-6" aria-hidden="true">{card.icon}</div>
          <h1 id="welcome-title" ref={titleRef} tabIndex={-1} className="text-3xl font-bold text-ink-50 mb-4 outline-none">
            {index === 0 && name ? `${card.title}, ${name}` : card.title}
          </h1>
          {card.body.map((p) => (
            <p key={p} className="text-lg text-ink-200 mb-3 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-2 mb-4" aria-hidden="true">
        {CARDS.map((c, n) => (
          <span key={c.title} className={`h-2 w-2 rounded-full ${n === index ? 'bg-mra-red' : 'bg-ink-600'}`} />
        ))}
      </div>
      <p className="sr-only" aria-live="polite">Card {index + 1} of {CARDS.length}</p>

      <div className="flex gap-3 p-5 max-w-2xl w-full mx-auto">
        {index > 0 && (
          <button type="button" onClick={back} className="btn-secondary flex-1">Back</button>
        )}
        <button type="button" onClick={next} className="btn-primary flex-1">
          {last ? "Let's go" : 'Next'}
        </button>
      </div>
    </div>
  );
}
